import React from "react";

interface CustomCheckboxProps {
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Function called when checkbox state changes */
  onChange: (checked: boolean) => void;
  /** Optional label text displayed to the right of the checkbox */
  label?: string;
  /** Unique identifier for the checkbox */
  id: string;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for the label */
  labelClassName?: string;
  /** ARIA label for accessibility when no visible label is provided */
  ariaLabel?: string;
}

export const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  checked,
  onChange,
  label,
  id,
  disabled = false,
  className = "",
  labelClassName = "",
  ariaLabel,
}) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      handleClick();
    }
  };

  // Unchecked checkbox icon
  const UncheckedIcon = () => (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.3784 4.49561C16.2481 4.53904 15.269 4.56141 13.8111 4.55055C13.2651 4.54649 13.1132 4.51798 11.7102 4.50679C10.3073 4.49561 7.65797 4.49561 6.28232 4.77791C4.90667 5.06021 4.88495 5.62482 4.85205 5.94825C4.79739 6.48554 4.62173 6.73166 4.57764 7.04982C4.52577 7.42418 4.42432 7.85034 4.39109 8.49622C4.34888 9.31657 4.29271 10.5918 4.25948 11.3255C4.23988 11.7583 4.1611 12.1053 4.08411 13.3905C4.02299 14.4106 4.07292 16.3668 4.0512 17.4062C4.02165 18.8207 3.98474 19.432 4.08345 19.6956C4.19242 19.9865 4.46643 20.1782 4.79546 20.3539C5.151 20.5438 5.45351 20.7271 5.78253 20.7711C5.96765 20.7959 6.93741 20.881 8.31898 20.8702C9.01186 20.8647 9.35706 20.7494 9.87165 20.6948C10.6177 20.6156 11.1792 20.6619 12.1574 20.629C13.07 20.5983 14.8294 20.6178 15.7681 20.607C16.9922 20.5928 17.4734 20.5086 17.814 20.4753C18.0979 20.4476 18.3513 20.3112 18.6694 20.2233C18.9475 20.1465 19.2067 20.0045 19.6233 19.5995C19.8796 19.3503 19.9964 18.9964 20.129 18.7105C20.2459 18.4584 20.3044 18.1843 20.3814 17.8228C20.4382 17.5557 20.4143 17.3282 20.4472 15.7963C20.4787 14.3298 20.4583 11.463 20.4258 9.97551C20.3901 8.34671 20.1951 7.74637 20.1405 7.26073C20.1035 6.93148 20.0201 6.62505 19.9322 6.32827C19.8325 5.99135 19.7569 5.68141 19.6035 5.41786C19.438 5.13324 19.1436 4.89175 18.88 4.70454C18.6165 4.6055 18.4191 4.53969 17.7232 4.50679C17.0924 4.49561 15.8981 4.49561 14.6675 4.49561"
        stroke="#171717"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );

  // Checked checkbox icon
  const CheckedIcon = () => (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.1889 4.67735C8.74543 4.6881 7.77125 4.66628 7.62567 4.62369C7.07479 4.62504 6.60126 4.62784 6.27683 4.63359C5.58095 4.6665 5.38353 4.7323 5.11998 4.83134C4.85643 5.01855 4.56204 5.26004 4.39646 5.54466C4.24313 5.80821 4.16749 6.11815 4.06776 6.45507C3.97991 6.75185 3.89652 7.05828 3.85949 7.38753C3.80487 7.87317 3.60989 8.47351 3.57422 10.1023C3.54165 11.5898 3.52134 14.4566 3.55284 15.9231C3.58574 17.455 3.56179 17.6825 3.61864 17.9496C3.69563 18.3111 3.75413 18.5852 3.871 18.8373C4.0036 19.1232 4.12045 19.4771 4.37672 19.7263C4.79326 20.1313 5.05252 20.2733 5.33056 20.3501C5.64872 20.438 5.90214 20.5744 6.18602 20.6021C6.52656 20.6354 7.00776 20.7196 8.2319 20.7338C9.17061 20.7446 10.93 20.7251 11.8426 20.7558C12.8208 20.7887 13.3823 20.7424 14.1283 20.8216C14.6429 20.8762 14.9881 20.9915 15.681 20.997C17.0626 21.0078 18.0324 20.9227 18.2175 20.8979C18.5465 20.8539 18.849 20.6706 19.2045 20.4807C19.5336 20.305 19.8076 20.1133 19.9166 19.8224C20.0153 19.5588 19.9783 18.9475 19.9488 17.533C19.9271 16.4936 19.977 14.5374 19.9159 13.5173C19.8389 12.2321 19.7601 11.8851 19.7405 11.4523C19.7073 10.7186 19.6511 9.44337 19.6089 8.62302C19.5757 7.97714 19.4742 7.55098 19.4224 7.17662C19.3783 6.85846 19.2026 6.61234 19.1479 6.07505C19.115 5.75162 19.0933 5.18701 17.7177 4.90471C16.342 4.62241 13.6927 4.62241 12.2898 4.63359C10.8868 4.64478 10.7349 4.67329 10.1889 4.67735Z"
        fill="black"
      />
      <path
        d="M7.62156 4.62241C7.75185 4.66584 8.73103 4.68821 10.1889 4.67735C10.7349 4.67329 10.8868 4.64478 12.2898 4.63359C13.6927 4.62241 16.342 4.62241 17.7177 4.90471C19.0933 5.18701 19.115 5.75162 19.1479 6.07505C19.2026 6.61234 19.3783 6.85846 19.4224 7.17662C19.4742 7.55098 19.5757 7.97714 19.6089 8.62302C19.6511 9.44337 19.7073 10.7186 19.7405 11.4523C19.7601 11.8851 19.8389 12.2321 19.9159 13.5173C19.977 14.5374 19.9271 16.4936 19.9488 17.533C19.9783 18.9475 20.0153 19.5588 19.9166 19.8224C19.8076 20.1133 19.5336 20.305 19.2045 20.4807C18.849 20.6706 18.5465 20.8539 18.2175 20.8979C18.0324 20.9227 17.0626 21.0078 15.681 20.997C14.9881 20.9915 14.6429 20.8762 14.1283 20.8216C13.3823 20.7424 12.8208 20.7887 11.8426 20.7558C10.93 20.7251 9.17061 20.7446 8.2319 20.7338C7.00776 20.7196 6.52656 20.6354 6.18602 20.6021C5.90214 20.5744 5.64872 20.438 5.33056 20.3501C5.05252 20.2733 4.79326 20.1313 4.37672 19.7263C4.12045 19.4771 4.0036 19.1232 3.871 18.8373C3.75413 18.5852 3.69563 18.3111 3.61864 17.9496C3.56179 17.6825 3.58574 17.455 3.55284 15.9231C3.52134 14.4566 3.54165 11.5898 3.57422 10.1023C3.60989 8.47351 3.80487 7.87317 3.85949 7.38753C3.89652 7.05828 3.97991 6.75185 4.06776 6.45507C4.16749 6.11815 4.24313 5.80821 4.39646 5.54466C4.56204 5.26004 4.85643 5.01855 5.11998 4.83134C5.38353 4.7323 5.58095 4.6665 6.27683 4.63359C6.90757 4.62241 8.10193 4.62241 9.33249 4.62241"
        stroke="black"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M7.37109 13.5202C7.52376 13.607 7.83041 13.8268 8.09396 13.9802C8.3578 14.1336 8.70693 14.3085 9.06985 14.5504C9.34771 14.7355 9.67295 14.8797 9.9365 14.9893C10.2407 15.1158 10.4626 15.2745 10.7262 15.4279C10.8625 15.5072 10.9897 15.6036 11.121 15.6477C11.3921 15.7387 11.5379 15.2324 11.7027 14.9689C11.852 14.7302 11.9327 14.4428 12.0538 14.1792C12.196 13.8697 12.4591 13.5439 12.6348 13.2579C12.7983 12.992 13.0514 12.7976 13.2162 12.5341C13.3853 12.2638 13.6212 11.8987 13.8302 11.5582C14.0266 11.238 14.2793 10.9117 14.6521 10.3747C14.9611 9.92955 15.0913 9.639 15.267 9.30998C15.443 8.98044 15.6178 8.71773 15.7935 8.39957"
        stroke="white"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div
      className={`flex items-center cursor-pointer ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${className}`}
      onClick={handleClick}
    >
      {/* Hidden native checkbox for accessibility */}
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={() => {}} // Handled by container click
        disabled={disabled}
        className="sr-only"
        aria-label={ariaLabel || label}
      />

      {/* Custom visual checkbox */}
      <div
        className={`
          ${
            !disabled
              ? "focus:outline-none focus:ring-1 focus:ring-black focus:ring"
              : ""
          }
          rounded
        `}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        role="checkbox"
        aria-checked={checked}
        aria-disabled={disabled}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-label={!label ? ariaLabel : undefined}
      >
        {checked ? <CheckedIcon /> : <UncheckedIcon />}
      </div>

      {/* Label */}
      {label && (
        <label
          id={`${id}-label`}
          htmlFor={id}
          onClick={(e) => e.preventDefault()}
          className={`
            ml-2
            ${labelClassName || "text-xs"}
            select-none
            ${disabled ? "cursor-not-allowed" : "cursor-pointer"}
          `}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default CustomCheckbox;
